import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { Grid, Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tabs, Tab, FormControl, Select, MenuItem, Popover, Button, Divider, CircularProgress, Checkbox, Chip, Collapse, Tooltip as MuiTooltip, Pagination, Autocomplete, TextField } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import PersonIcon from '@mui/icons-material/Person'
import StorefrontIcon from '@mui/icons-material/Storefront'
import { styled, alpha } from '@mui/material/styles'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid, AreaChart, Area } from 'recharts'
import spotifyLogo from '../../assets/img/spotify.png'
import appleMusicLogo from '../../assets/img/apple_music.png'
import amazonMusicLogo from '../../assets/img/amazon_music.png'
import pandoraLogo from '../../assets/img/pandora.png'
import deezerLogo from '../../assets/img/deezer.png'
import soundcloudLogo from '../../assets/img/soundcloud.png'
import youtubeLogo from '../../assets/img/youtube.png'
import youtubeShortsLogo from '../../assets/img/youtube_shorts.png'
import youtubeContentIdLogo from '../../assets/img/youtube_content_id.png'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import * as actions from './actions'
import moment from 'moment'

const CHART_COLORS = ['#12B4A1', '#0596B3', '#667eea', '#764ba2', '#4facfe', '#f59e0b', '#ef4444', '#10b981']

const STORE_OPTIONS = [
    { value: 'amazon_music', label: 'Amazon Music', logo: amazonMusicLogo },
    { value: 'apple_music', label: 'Apple Music', logo: appleMusicLogo },
    { value: 'deezer', label: 'Deezer', logo: deezerLogo },
    { value: 'pandora', label: 'Pandora', logo: pandoraLogo },
    { value: 'soundcloud', label: 'SoundCloud', logo: soundcloudLogo },
    { value: 'spotify', label: 'Spotify', logo: spotifyLogo },
    { value: 'youtube', label: 'YouTube', logo: youtubeLogo },
    { value: 'youtube_content_id', label: 'YouTube Content ID', logo: youtubeContentIdLogo },
    { value: 'youtube_shorts', label: 'YouTube Shorts', logo: youtubeShortsLogo },
]

const PageContainer = styled(Box)({
    padding: '24px',
    minHeight: 'calc(100vh - 100px)',
})

const MetricCard = styled(Box)(({ gradient }) => ({
    background: gradient || 'linear-gradient(135deg, #139979 0%, #12B4A1 100%)',
    borderRadius: '16px',
    padding: '24px',
    minHeight: '120px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    color: '#fff',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '-50%',
        right: '-20%',
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.1)',
    },
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.25)',
    },
}))

const MetricValue = styled(Typography)({
    fontSize: '2.5rem',
    fontWeight: 700,
    marginTop: '8px',
    fontFamily: "'DM Sans', sans-serif",
})

const MetricLabel = styled(Typography)({
    fontSize: '0.875rem',
    opacity: 0.9,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontFamily: "'DM Sans', sans-serif",
})

const ChartCard = styled(Box)({
    background: 'rgba(30, 41, 59, 0.5)',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
})

const ChartTitle = styled(Typography)({
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '20px',
    color: '#F1F5F9',
    fontFamily: "'DM Sans', sans-serif",
})

const StyledTableContainer = styled(TableContainer)({
    borderRadius: '16px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.2)',
    backgroundColor: 'rgba(30, 41, 59, 0.5)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    overflow: 'hidden',
    '& .MuiTable-root': {
        fontFamily: "'DM Sans', sans-serif",
        backgroundColor: 'transparent',
    },
    '& .MuiTableHead-root': {
        background: 'linear-gradient(135deg, #139979 0%, #12B4A1 50%, #0596B3 100%)',
        '& .MuiTableCell-head': {
            color: 'white',
            fontWeight: 700,
            fontSize: '0.9375rem',
            fontFamily: "'DM Sans', sans-serif",
            padding: '16px',
        },
    },
    '& .MuiTableBody-root': {
        backgroundColor: 'transparent',
        '& .MuiTableRow-root': {
            backgroundColor: 'transparent !important',
            transition: 'background-color 0.2s ease',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            '&:nth-of-type(odd)': {
                backgroundColor: 'rgba(255, 255, 255, 0.02) !important',
            },
            '&:hover': {
                backgroundColor: 'rgba(18, 180, 161, 0.08) !important',
            },
        },
    },
    '& .MuiTableCell-body': {
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '0.875rem',
        color: '#F1F5F9',
        padding: '16px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
    },
})

const StyledTabs = styled(Tabs)({
    background: 'rgba(30, 41, 59, 0.5)',
    backdropFilter: 'blur(20px)',
    minHeight: '40px',
    borderBottom: '2px solid rgba(18, 180, 161, 0.1)',
    '& .MuiTabs-indicator': {
        background: 'linear-gradient(90deg, #139979 0%, #12B4A1 50%, #0596B3 100%)',
        height: '2px',
        borderRadius: '2px 2px 0 0',
    },
    '& .MuiTabs-flexContainer': {
        justifyContent: 'flex-start',
    },
})

const StyledTab = styled(Tab)({
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'none',
    minHeight: '40px',
    padding: '6px 16px',
    transition: 'all 0.3s ease',
    color: '#94A3B8',
    '&.Mui-selected': {
        background: 'linear-gradient(135deg, rgba(18, 180, 161, 0.08) 0%, rgba(5, 150, 179, 0.08) 100%)',
        color: '#F1F5F9',
    },
    '&:hover': {
        background: 'rgba(18, 180, 161, 0.05)',
    },
})

const tooltipStyle = {
    backgroundColor: 'rgba(15, 23, 42, 0.95)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    color: '#F1F5F9',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.8rem',
}

function groupBy(data, key) {
    const map = {}
    data.forEach((t) => {
        if (!map[t[key]]) {
            map[t[key]] = { name: t[key], tracks: 0, streams: 0 }
        }
        map[t[key]].tracks += 1
        map[t[key]].streams += t.streams
    })
    return Object.values(map).sort((a, b) => b.streams - a.streams)
}

function parseDDMMYYYY(dateStr) {
    const parts = dateStr.split('-')
    if (parts[0].length === 4) {
        return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]))
    }
    const [dd, mm, yyyy] = parts
    return new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd))
}

function groupRangesByMonth(ranges) {
    const groups = {}
    ranges.forEach((range) => {
        const date = parseDDMMYYYY(range.startDate)
        const label = moment(date).format('MMMM YYYY')
        if (!groups[label]) {
            groups[label] = { label, sortDate: date, ranges: [] }
        }
        groups[label].ranges.push(range)
    })
    return Object.values(groups).sort((a, b) => b.sortDate - a.sortDate)
}

export default function StreamingStats() {
    const [activeTab, setActiveTab] = useState(0)
    const [tablePage, setTablePage] = useState(1)
    const [store, setStore] = useState('spotify')

    // API-driven state
    const [tracks, setTracks] = useState([])
    const [totalStreams, setTotalStreams] = useState(0)
    const [availableRanges, setAvailableRanges] = useState([])
    const [availableStores, setAvailableStores] = useState(null)
    const [loading, setLoading] = useState(true)

    // User filter state
    const [labelUsers, setLabelUsers] = useState([])
    const [filterUserId, setFilterUserId] = useState(null)

    // Separate state for "Filter by Sub Label Acc." tab (independent from Songs/Labels)
    const [userTracks, setUserTracks] = useState([])
    const [userTotalStreams, setUserTotalStreams] = useState(0)
    const [userMultiFileData, setUserMultiFileData] = useState({})
    const [userLoading, setUserLoading] = useState(false)

    // Separate state for "Store Summary" tab
    const [storeSummary, setStoreSummary] = useState([]) // [{ store, label, logo, totalStreams, topTrack }]
    const [storeLoading, setStoreLoading] = useState(false)

    // Multi-select state
    const [selectedRanges, setSelectedRanges] = useState([])
    const [pendingRanges, setPendingRanges] = useState([])
    const [multiFileData, setMultiFileData] = useState({})
    const [expandedMonths, setExpandedMonths] = useState({})
    const [multiLoading, setMultiLoading] = useState(false)

    // Date range picker state
    const [dateAnchorEl, setDateAnchorEl] = useState(null)
    const [selectedYear, setSelectedYear] = useState(null)

    const datePopoverOpen = Boolean(dateAnchorEl)
    const isMultiSelect = selectedRanges.length > 1
    const storeTabIndex = labelUsers.length > 0 ? 3 : 2
    const groupedRanges = useMemo(() => groupRangesByMonth(availableRanges), [availableRanges])

    const availableYears = useMemo(() => {
        const years = new Set()
        availableRanges.forEach((range) => {
            const date = parseDDMMYYYY(range.startDate)
            years.add(date.getFullYear())
        })
        return [...years].sort((a, b) => b - a)
    }, [availableRanges])

    const filteredGroupedRanges = useMemo(() => {
        if (!selectedYear) return groupedRanges
        return groupedRanges.filter((g) => g.sortDate.getFullYear() === selectedYear)
    }, [groupedRanges, selectedYear])

    const fetchData = useCallback((filename) => {
        setLoading(true)
        actions.getAnalyticsDataLabel(filename)
            .then((res) => {
                const data = Array.isArray(res.body) ? res.body : []
                setTracks(data)
                setTotalStreams(data.reduce((sum, t) => sum + t.streams, 0))
                setLoading(false)
            })
            .catch(() => {
                setTracks([])
                setTotalStreams(0)
                setLoading(false)
            })
    }, [])

    const fetchMultipleFiles = useCallback((ranges) => {
        setMultiLoading(true)
        setLoading(true)
        Promise.all(
            ranges.map((range) =>
                actions.getAnalyticsDataLabel(range.filename)
                    .then((res) => ({
                        filename: range.filename,
                        label: range.label,
                        startDate: range.startDate,
                        data: Array.isArray(res.body) ? res.body : [],
                    }))
                    .catch(() => ({
                        filename: range.filename,
                        label: range.label,
                        startDate: range.startDate,
                        data: [],
                    }))
            )
        ).then((results) => {
            const dataMap = {}
            results.forEach((r) => {
                dataMap[r.filename] = { label: r.label, startDate: r.startDate, data: r.data }
            })
            setMultiFileData(dataMap)
            setMultiLoading(false)
            setLoading(false)
        })
    }, [])

    // Fetch functions for the Users tab (filtered by userId)
    const fetchUserData = useCallback((filename, userId) => {
        if (!userId) return
        setUserLoading(true)
        actions.getAnalyticsDataLabel(filename, userId)
            .then((res) => {
                const data = Array.isArray(res.body) ? res.body : []
                setUserTracks(data)
                setUserTotalStreams(data.reduce((sum, t) => sum + t.streams, 0))
                setUserLoading(false)
            })
            .catch(() => {
                setUserTracks([])
                setUserTotalStreams(0)
                setUserLoading(false)
            })
    }, [])

    const fetchUserMultipleFiles = useCallback((ranges, userId) => {
        if (!userId) return
        setUserLoading(true)
        Promise.all(
            ranges.map((range) =>
                actions.getAnalyticsDataLabel(range.filename, userId)
                    .then((res) => ({
                        filename: range.filename,
                        label: range.label,
                        startDate: range.startDate,
                        data: Array.isArray(res.body) ? res.body : [],
                    }))
                    .catch(() => ({
                        filename: range.filename,
                        label: range.label,
                        startDate: range.startDate,
                        data: [],
                    }))
            )
        ).then((results) => {
            const dataMap = {}
            results.forEach((r) => {
                dataMap[r.filename] = { label: r.label, startDate: r.startDate, data: r.data }
            })
            setUserMultiFileData(dataMap)
            setUserLoading(false)
        })
    }, [])

    // Fetch functions for the Stores tab (filtered by store)
    const fetchAllStoresSummary = useCallback((ranges) => {
        if (!availableStores || availableStores.size === 0 || ranges.length === 0) return
        setStoreLoading(true)
        const storeList = STORE_OPTIONS.filter(opt => availableStores.has(opt.value))
        Promise.all(
            storeList.map((opt) =>
                Promise.all(
                    ranges.map((range) => {
                        const filename = `${opt.value}_${range.startDate}_to_${range.endDate}.xlsx`
                        return actions.getAnalyticsDataLabel(filename)
                            .then((res) => (Array.isArray(res.body) ? res.body : []))
                            .catch(() => [])
                    })
                ).then((results) => {
                    const allTracks = results.flat()
                    const totalStreams = allTracks.reduce((sum, t) => sum + t.streams, 0)
                    const sorted = [...allTracks].sort((a, b) => b.streams - a.streams)
                    return {
                        store: opt.value,
                        label: opt.label,
                        logo: opt.logo,
                        totalStreams,
                        topTrack: sorted.length > 0 ? sorted[0].title : '-',
                        trackCount: allTracks.length,
                    }
                })
            )
        ).then((summaries) => {
            setStoreSummary(summaries.filter(s => s.totalStreams > 0).sort((a, b) => b.totalStreams - a.totalStreams))
            setStoreLoading(false)
        })
    }, [availableStores])

    const fetchDateRanges = useCallback((storeName) => {
        setLoading(true)
        setMultiFileData({})
        actions.getAvailableDateRanges(storeName)
            .then((res) => {
                const ranges = Array.isArray(res.body) ? res.body : []
                setAvailableRanges(ranges)
                if (ranges.length > 0) {
                    const mostRecent = [...ranges].sort((a, b) => parseDDMMYYYY(b.startDate) - parseDDMMYYYY(a.startDate))[0]
                    setSelectedRanges([mostRecent])
                    setPendingRanges([mostRecent])
                    fetchData(mostRecent.filename)
                    const grouped = groupRangesByMonth(ranges)
                    const expanded = {}
                    grouped.forEach((g) => { expanded[g.label] = true })
                    setExpandedMonths(expanded)
                    const years = new Set()
                    ranges.forEach((r) => {
                        const d = parseDDMMYYYY(r.startDate)
                        years.add(d.getFullYear())
                    })
                    const sortedYears = [...years].sort((a, b) => b - a)
                    if (sortedYears.length > 0) setSelectedYear(sortedYears[0])
                } else {
                    setSelectedRanges([])
                    setPendingRanges([])
                    setTracks([])
                    setTotalStreams(0)
                    setSelectedYear(null)
                    setLoading(false)
                }
            })
            .catch(() => {
                setAvailableRanges([])
                setSelectedRanges([])
                setTracks([])
                setTotalStreams(0)
                setSelectedYear(null)
                setLoading(false)
            })
    }, [])

    const fetchAvailableStores = useCallback(() => {
        actions.getAvailableDateRanges()
            .then((res) => {
                const allFiles = Array.isArray(res.body) ? res.body : []
                setAvailableStores(new Set(allFiles.map(f => f.store)))
            })
            .catch(() => setAvailableStores(new Set()))
    }, [])

    // On mount: fetch label users and initial data
    useEffect(() => {
        actions.getLabelUsersForFilter()
            .then((res) => {
                setLabelUsers(Array.isArray(res.body) ? res.body : [])
            })
            .catch(() => setLabelUsers([]))

        fetchDateRanges(store)
        fetchAvailableStores()
    }, [])

    useEffect(() => {
        fetchDateRanges(store)
    }, [store, fetchDateRanges])

    useEffect(() => {
        if (availableStores === null) return
        if (availableStores.has(store)) return
        const firstAvailable = STORE_OPTIONS.find(opt => availableStores.has(opt.value))
        if (firstAvailable) {
            setStore(firstAvailable.value)
        }
    }, [availableStores])

    const handleStoreChange = (e) => {
        setStore(e.target.value)
        setTablePage(1)
        // Clear user-filtered data since date ranges will change (will re-fetch via useEffect if filterUserId is set)
        setUserTracks([])
        setUserTotalStreams(0)
        setUserMultiFileData({})
        // Clear store summary data since date ranges will change
        setStoreSummary([])
    }

    const handleTabChange = (e, newTab) => {
        setActiveTab(newTab)
        setTablePage(1)
    }

    const handleDateChipClick = (event) => {
        setPendingRanges([...selectedRanges])
        setDateAnchorEl(event.currentTarget)
    }

    const handleDatePopoverClose = () => {
        setDateAnchorEl(null)
    }

    const handleRangeToggle = (range) => {
        setPendingRanges((prev) => {
            const exists = prev.some((r) => r.filename === range.filename)
            if (exists) {
                return prev.filter((r) => r.filename !== range.filename)
            }
            if (prev.length >= 5) return prev
            return [...prev, range]
        })
    }

    const toggleMonthExpanded = (monthLabel) => {
        setExpandedMonths((prev) => ({ ...prev, [monthLabel]: !prev[monthLabel] }))
    }

    const handleYearChange = (e) => {
        const year = e.target.value
        setSelectedYear(year)
        const newExpanded = { ...expandedMonths }
        groupedRanges
            .filter((g) => g.sortDate.getFullYear() === year)
            .forEach((g) => { newExpanded[g.label] = true })
        setExpandedMonths(newExpanded)
    }

    const handleApply = () => {
        if (pendingRanges.length === 0) return
        setSelectedRanges([...pendingRanges])
        if (pendingRanges.length === 1) {
            fetchData(pendingRanges[0].filename)
        } else {
            fetchMultipleFiles(pendingRanges)
        }
        // User data will be re-fetched by the useEffect when selectedRanges changes
        // Clear store summary so it refetches when tab is active
        setStoreSummary([])
        setDateAnchorEl(null)
    }

    // Pick the right data source based on active tab
    const isUsersTab = labelUsers.length > 0 && activeTab === 2
    const isStoresTab = activeTab === storeTabIndex
    const activeTracks = isUsersTab ? userTracks : tracks
    const activeTotalStreams = isUsersTab ? userTotalStreams : totalStreams
    const activeMultiFileData = isUsersTab ? userMultiFileData : multiFileData

    // Fetch store summary only when user clicks the Store Summary tab
    useEffect(() => {
        if (isStoresTab && selectedRanges.length > 0 && storeSummary.length === 0 && !storeLoading && availableStores && availableStores.size > 0) {
            fetchAllStoresSummary(selectedRanges)
        }
    }, [isStoresTab]) // eslint-disable-line react-hooks/exhaustive-deps

    // Re-fetch user-filtered data when selectedRanges or filterUserId changes
    useEffect(() => {
        if (!filterUserId) {
            setUserTracks([])
            setUserTotalStreams(0)
            setUserMultiFileData({})
            return
        }
        if (selectedRanges.length === 0) return
        if (selectedRanges.length === 1) {
            fetchUserData(selectedRanges[0].filename, filterUserId)
        } else {
            fetchUserMultipleFiles(selectedRanges, filterUserId)
        }
    }, [selectedRanges, filterUserId, fetchUserData, fetchUserMultipleFiles])

    // Single-file chart data
    const { summaryCards, barData, barTitle, pieData, pieTitle, artistData, showArtistChart, tableTitle, tableColumns, tableRows } = useMemo(() => {
        if (activeTracks.length === 0) {
            return {
                summaryCards: [
                    { label: 'Total Tracks', value: '0' },
                    { label: 'Total Streams', value: '0' },
                    { label: 'Top Track', value: '-', small: true },
                    { label: 'Avg. Streams', value: '0' },
                ],
                barTitle: 'No data',
                barData: [],
                pieTitle: 'No data',
                pieData: [],
                artistData: [],
                showArtistChart: false,
                tableTitle: 'No data',
                tableColumns: [],
                tableRows: [],
            }
        }

        if (activeTab === 1) {
            const grouped = groupBy(activeTracks, 'label')
            const top = grouped[0]
            return {
                summaryCards: [
                    { label: 'Total Labels', value: grouped.length },
                    { label: 'Total Streams', value: activeTotalStreams.toLocaleString() },
                    { label: 'Top Label', value: top.name, small: true },
                    { label: 'Avg. Streams/Label', value: Math.round(activeTotalStreams / grouped.length).toLocaleString() },
                ],
                barTitle: `Top ${Math.min(grouped.length, 15)} Labels by Streams`,
                barData: grouped.slice(0, 15).map((l) => ({
                    name: l.name.length > 20 ? l.name.substring(0, 20) + '...' : l.name,
                    streams: l.streams,
                })),
                pieTitle: 'Label Stream Share',
                pieData: (() => {
                    const top5 = grouped.slice(0, 5)
                    const othersStreams = grouped.slice(5).reduce((sum, l) => sum + l.streams, 0)
                    const result = top5.map((l) => ({
                        name: l.name.length > 25 ? l.name.substring(0, 25) + '...' : l.name,
                        value: l.streams,
                    }))
                    if (othersStreams > 0) result.push({ name: 'Others', value: othersStreams })
                    return result
                })(),
                artistData: [],
                showArtistChart: false,
                tableTitle: 'Label Details',
                tableColumns: ['No.', 'Label', 'Tracks', 'Streams'],
                tableRows: grouped.map((l, i) => ({
                    index: i + 1,
                    cells: [l.name, l.tracks, l.streams.toLocaleString()],
                })),
            }
        }

        // Songs / Users tab (default)
        const artistMap = {}
        activeTracks.forEach((t) => {
            artistMap[t.artist] = (artistMap[t.artist] || 0) + t.streams
        })
        const artistChartData = Object.entries(artistMap)
            .map(([name, streams]) => ({
                name: name.length > 30 ? name.substring(0, 30) + '...' : name,
                fullName: name,
                streams,
            }))
            .sort((a, b) => b.streams - a.streams)
            .slice(0, 10)

        return {
            summaryCards: [
                { label: 'Total Tracks', value: activeTracks.length },
                { label: 'Total Streams', value: activeTotalStreams.toLocaleString() },
                { label: 'Top Track', value: activeTracks[0].title, small: true },
                { label: 'Avg. Streams', value: Math.round(activeTotalStreams / activeTracks.length).toLocaleString() },
            ],
            barTitle: `Top ${Math.min(activeTracks.length, 15)} Tracks by Streams`,
            barData: activeTracks.slice(0, 15).map((t) => ({
                name: t.title.length > 20 ? t.title.substring(0, 20) + '...' : t.title,
                streams: t.streams,
            })),
            pieTitle: 'Stream Distribution',
            pieData: (() => {
                const top5 = activeTracks.slice(0, 5)
                const othersStreams = activeTracks.slice(5).reduce((sum, t) => sum + t.streams, 0)
                const result = top5.map((t) => ({
                    name: t.title.length > 25 ? t.title.substring(0, 25) + '...' : t.title,
                    value: t.streams,
                }))
                if (othersStreams > 0) result.push({ name: 'Others', value: othersStreams })
                return result
            })(),
            artistData: artistChartData,
            showArtistChart: true,
            tableTitle: 'Track Details',
            tableColumns: ['No.', 'Title', 'Artist', 'Label', 'Streams'],
            tableRows: activeTracks.map((t, i) => ({
                index: i + 1,
                cells: [t.title, t.artist, t.label, t.streams.toLocaleString()],
            })),
        }
    }, [activeTab, activeTracks, activeTotalStreams])

    // Multi-file comparison data
    const { comparisonData, multiSummaryCards, avgStreamsPerPeriod } = useMemo(() => {
        if (!isMultiSelect || Object.keys(activeMultiFileData).length === 0) {
            return { comparisonData: null, multiSummaryCards: [], avgStreamsPerPeriod: 0 }
        }

        const sortedRanges = selectedRanges
            .map((r) => ({
                ...r,
                parsedDate: parseDDMMYYYY(r.startDate),
                fileData: activeMultiFileData[r.filename],
            }))
            .filter((r) => r.fileData)
            .sort((a, b) => a.parsedDate - b.parsedDate)

        if (sortedRanges.length === 0) {
            return { comparisonData: null, multiSummaryCards: [], avgStreamsPerPeriod: 0 }
        }

        const overallData = sortedRanges.map((r) => ({
            period: r.fileData.label,
            streams: r.fileData.data.reduce((sum, t) => sum + t.streams, 0),
        }))

        const songCounts = {}
        const songStreams = {}
        sortedRanges.forEach((r) => {
            r.fileData.data.forEach((t) => {
                songCounts[t.title] = (songCounts[t.title] || 0) + 1
                songStreams[t.title] = (songStreams[t.title] || 0) + t.streams
            })
        })
        const topSongs = Object.keys(songCounts)
            .sort((a, b) => {
                if (songCounts[b] !== songCounts[a]) return songCounts[b] - songCounts[a]
                return songStreams[b] - songStreams[a]
            })
            .slice(0, 10)

        const perSongData = sortedRanges.map((r) => {
            const entry = { period: r.fileData.label }
            const trackMap = {}
            r.fileData.data.forEach((t) => {
                trackMap[t.title] = (trackMap[t.title] || 0) + t.streams
            })
            topSongs.forEach((song) => {
                entry[song] = trackMap[song] || 0
            })
            return entry
        })

        const totalStreamsAll = overallData.reduce((sum, d) => sum + d.streams, 0)
        let topTrackOverall = '-'
        if (Object.keys(songStreams).length > 0) {
            topTrackOverall = Object.entries(songStreams).sort((a, b) => b[1] - a[1])[0][0]
        }
        const avgStreams = sortedRanges.length > 0 ? Math.round(totalStreamsAll / sortedRanges.length) : 0

        return {
            comparisonData: { overallData, perSongData, topSongs },
            multiSummaryCards: [
                { label: 'Periods Compared', value: sortedRanges.length },
                { label: 'Total Streams', value: totalStreamsAll.toLocaleString() },
                { label: 'Top Track Overall', value: topTrackOverall, small: true },
            ],
            avgStreamsPerPeriod: avgStreams,
        }
    }, [isMultiSelect, selectedRanges, activeMultiFileData])

    const CARD_GRADIENTS = [
        'linear-gradient(135deg, #139979 0%, #12B4A1 100%)',
        'linear-gradient(135deg, #0596B3 0%, #12B4A1 100%)',
        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        'linear-gradient(135deg, #0596B3 0%, #139979 100%)',
    ]

    const selectDarkSx = {
        minWidth: 220,
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: 'rgba(30, 41, 59, 0.5)',
            backdropFilter: 'blur(20px)',
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 600,
            color: '#F1F5F9',
            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
            '&:hover fieldset': { borderColor: '#12B4A1' },
            '&.Mui-focused fieldset': { borderColor: '#12B4A1' },
        },
        '& .MuiSelect-select': {
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 600,
            color: '#F1F5F9',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        '& .MuiSelect-icon': {
            color: '#94A3B8',
        },
    }

    const menuPropsDark = {
        autoFocus: false,
        disablePortal: false,
        PaperProps: {
            sx: {
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                mt: 1,
                minWidth: 240,
                backgroundColor: '#1E293B',
                border: '1px solid rgba(255, 255, 255, 0.1)',
            },
        },
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
    }

    if (loading && tracks.length === 0 && availableRanges.length === 0) {
        return (
            <PageContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress sx={{ color: '#12B4A1' }} />
                </Box>
            </PageContainer>
        )
    }

    if (!loading && tracks.length === 0 && availableRanges.length === 0) {
        return (
            <PageContainer>
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#94A3B8', fontFamily: "'DM Sans', sans-serif" }}>
                        Select Store
                    </Typography>
                    <FormControl size="small" sx={selectDarkSx}>
                        <Select
                            value={store}
                            onChange={handleStoreChange}
                            IconComponent={KeyboardArrowDownIcon}
                            renderValue={(value) => {
                                const opt = STORE_OPTIONS.find(o => o.value === value)
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {opt?.logo && <img src={opt.logo} alt={opt.label} style={{ width: 20, height: 20, objectFit: 'contain' }} />}
                                        {opt?.label || value}
                                    </Box>
                                )
                            }}
                            MenuProps={menuPropsDark}
                        >
                            {STORE_OPTIONS.map(opt => {
                                const isStoreDisabled = availableStores !== null && !availableStores.has(opt.value)
                                return (
                                    <MenuItem key={opt.value} value={opt.value} disabled={isStoreDisabled}
                                        sx={{
                                            fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
                                            display: 'flex', alignItems: 'center', gap: 1,
                                            color: isStoreDisabled ? '#475569' : '#F1F5F9',
                                            '&.Mui-selected': { backgroundColor: 'rgba(18, 180, 161, 0.15)', color: '#F1F5F9' },
                                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                                        }}
                                    >
                                        {opt.logo && <img src={opt.logo} alt={opt.label} style={{ width: 20, height: 20, objectFit: 'contain', filter: isStoreDisabled ? 'grayscale(100%)' : 'none' }} />}
                                        {opt.label}
                                    </MenuItem>
                                )
                            })}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', flexDirection: 'column', gap: 2 }}>
                    <CalendarTodayIcon sx={{ fontSize: 64, color: '#334155' }} />
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, color: '#94A3B8', fontFamily: "'DM Sans', sans-serif" }}>
                        No analytics data available
                    </Typography>
                    <Typography sx={{ fontSize: '0.875rem', color: '#64748B', fontFamily: "'DM Sans', sans-serif" }}>
                        Analytics data will appear here once files are uploaded by the admin.
                    </Typography>
                </Box>
            </PageContainer>
        )
    }

    return (
        <PageContainer>
            {/* Analytics Disclaimer */}
            <Box sx={{
                background: 'rgba(245, 158, 11, 0.1)',
                borderRadius: '16px',
                padding: '16px 20px',
                mb: 3,
                border: '1px solid rgba(245, 158, 11, 0.3)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '14px',
            }}>
                <InfoOutlinedIcon sx={{ color: '#f59e0b', fontSize: 24, mt: '2px', flexShrink: 0 }} />
                <Box>
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', fontWeight: 700, color: '#f59e0b', mb: 0.5 }}>
                        Analytics Data May Differ from Royalty Reports
                    </Typography>
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', fontWeight: 500, color: '#94A3B8', lineHeight: 1.6 }}>
                        The stream counts shown in Analytics are intended to provide near real-time performance insights and may differ from the figures shown in your Royalty Reports. Many Digital Service Providers (DSPs), including Spotify, Apple Music, YouTube, and Deezer, include non-royalty-bearing streams in their analytics data.
                    </Typography>
                </Box>
            </Box>

            {/* Store Selector, User Filter & Date Range */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#94A3B8', fontFamily: "'DM Sans', sans-serif" }}>
                    Select Store
                </Typography>
                <FormControl size="small" sx={{ ...selectDarkSx, ...(isStoresTab && { opacity: 0.5 }) }}>
                    <Select
                        value={store}
                        onChange={handleStoreChange}
                        disabled={isStoresTab}
                        IconComponent={KeyboardArrowDownIcon}
                        renderValue={(value) => {
                            const opt = STORE_OPTIONS.find(o => o.value === value)
                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {opt?.logo && <img src={opt.logo} alt={opt.label} style={{ width: 20, height: 20, objectFit: 'contain' }} />}
                                    {opt?.label || value}
                                </Box>
                            )
                        }}
                        MenuProps={menuPropsDark}
                    >
                        {STORE_OPTIONS.map(opt => {
                            const isStoreDisabled = availableStores !== null && !availableStores.has(opt.value)
                            const menuItem = (
                                <MenuItem
                                    key={opt.value}
                                    value={opt.value}
                                    disabled={isStoreDisabled}
                                    sx={{
                                        fontFamily: "'DM Sans', sans-serif",
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: isStoreDisabled ? '#475569' : '#F1F5F9',
                                        '&.Mui-selected': { backgroundColor: 'rgba(18, 180, 161, 0.15)', color: '#F1F5F9' },
                                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                                        '&.Mui-disabled': { opacity: 0.5 },
                                    }}
                                >
                                    {opt.logo && <img src={opt.logo} alt={opt.label} style={{ width: 20, height: 20, objectFit: 'contain', filter: isStoreDisabled ? 'grayscale(100%)' : 'none' }} />}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
                                        {opt.label}
                                        {isStoreDisabled && (
                                            <Typography component="span" sx={{ fontSize: '0.7rem', fontStyle: 'italic', color: '#475569', ml: 1 }}>
                                                No data
                                            </Typography>
                                        )}
                                    </Box>
                                </MenuItem>
                            )
                            return isStoreDisabled ? (
                                <MuiTooltip key={opt.value} title="No analytics available for this store" placement="right">
                                    <span>{menuItem}</span>
                                </MuiTooltip>
                            ) : menuItem
                        })}
                    </Select>
                </FormControl>

                {/* Date Range Picker */}
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#94A3B8', fontFamily: "'DM Sans', sans-serif" }}>
                    Select Date
                </Typography>
                {selectedRanges.length > 0 && (
                    <Box
                        onClick={handleDateChipClick}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1,
                            borderRadius: '8px',
                            backgroundColor: 'rgba(30, 41, 59, 0.5)',
                            backdropFilter: 'blur(20px)',
                            color: '#F1F5F9',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            cursor: 'pointer',
                            fontFamily: "'DM Sans', sans-serif",
                            fontWeight: 600,
                            fontSize: '0.8125rem',
                            transition: 'all 0.2s ease',
                            userSelect: 'none',
                            '&:hover': {
                                borderColor: '#12B4A1',
                            },
                        }}
                    >
                        <CalendarTodayIcon sx={{ fontSize: 16, color: '#12B4A1' }} />
                        {selectedRanges.length === 1 ? selectedRanges[0].label : `${selectedRanges.length} ranges selected`}
                        <KeyboardArrowDownIcon sx={{ fontSize: 18, ml: 0.5, color: '#94A3B8' }} />
                    </Box>
                )}

                <Popover
                    open={datePopoverOpen}
                    anchorEl={dateAnchorEl}
                    onClose={handleDatePopoverClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                        paper: {
                            sx: {
                                borderRadius: '12px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                                mt: 1,
                                overflow: 'hidden',
                                minWidth: 520,
                                backgroundColor: '#1E293B',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                            },
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', minHeight: 280, backgroundColor: '#1E293B' }}>
                        {/* Left Panel - Grouped Ranges with Checkboxes */}
                        <Box sx={{ width: '55%', borderRight: '1px solid rgba(255, 255, 255, 0.06)', py: 1, backgroundColor: 'rgba(15, 23, 42, 0.5)', maxHeight: 380, overflowY: 'auto' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1 }}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: "'DM Sans', sans-serif" }}>
                                    Date Ranges
                                </Typography>
                                {availableYears.length > 1 ? (
                                    <FormControl size="small" sx={{ minWidth: 90 }}>
                                        <Select
                                            value={selectedYear || ''}
                                            onChange={handleYearChange}
                                            IconComponent={KeyboardArrowDownIcon}
                                            sx={{
                                                fontSize: '0.75rem', fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
                                                color: '#12B4A1',
                                                backgroundColor: 'rgba(18, 180, 161, 0.1)',
                                                borderRadius: '4px',
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(18, 180, 161, 0.2)' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#12B4A1' },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#12B4A1' },
                                                '& .MuiSelect-select': { py: 0.5, px: 1.5, pr: '28px !important' },
                                                '& .MuiSelect-icon': { color: '#12B4A1', fontSize: 20 },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        backgroundColor: '#1E293B',
                                                        borderRadius: '8px',
                                                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                                                        mt: 0.5,
                                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                                    },
                                                },
                                            }}
                                        >
                                            {availableYears.map((year) => (
                                                <MenuItem key={year} value={year} sx={{ fontSize: '0.8125rem', fontWeight: 600, fontFamily: "'DM Sans', sans-serif", color: '#F1F5F9' }}>
                                                    {year}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : availableYears.length === 1 ? (
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#12B4A1', fontFamily: "'DM Sans', sans-serif" }}>
                                        {availableYears[0]}
                                    </Typography>
                                ) : null}
                            </Box>
                            {filteredGroupedRanges.map((group) => (
                                <Box key={group.label}>
                                    <Box
                                        onClick={() => toggleMonthExpanded(group.label)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            px: 2.5,
                                            py: 1,
                                            cursor: 'pointer',
                                            backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.06)' },
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: '#F1F5F9', fontFamily: "'DM Sans', sans-serif" }}>
                                            {moment(group.sortDate).format('MMMM')}
                                        </Typography>
                                        {expandedMonths[group.label] ? (
                                            <ExpandLessIcon sx={{ fontSize: 18, color: '#94A3B8' }} />
                                        ) : (
                                            <ExpandMoreIcon sx={{ fontSize: 18, color: '#94A3B8' }} />
                                        )}
                                    </Box>
                                    <Collapse in={expandedMonths[group.label]}>
                                        {group.ranges.map((range) => {
                                            const isSelected = pendingRanges.some((r) => r.filename === range.filename)
                                            const isDisabled = !isSelected && pendingRanges.length >= 5
                                            return (
                                                <Box
                                                    key={range.filename}
                                                    onClick={() => !isDisabled && handleRangeToggle(range)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        px: 1,
                                                        py: 0.5,
                                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                        opacity: isDisabled ? 0.5 : 1,
                                                        backgroundColor: isSelected ? 'rgba(18, 180, 161, 0.1)' : 'transparent',
                                                        borderLeft: isSelected ? '3px solid #12B4A1' : '3px solid transparent',
                                                        transition: 'all 0.15s ease',
                                                        '&:hover': {
                                                            backgroundColor: isDisabled ? 'transparent' : isSelected ? 'rgba(18, 180, 161, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                                                        },
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={isSelected}
                                                        disabled={isDisabled}
                                                        size="small"
                                                        sx={{
                                                            color: '#475569',
                                                            '&.Mui-checked': { color: '#12B4A1' },
                                                            p: 0.5,
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={() => !isDisabled && handleRangeToggle(range)}
                                                    />
                                                    <Typography sx={{
                                                        fontSize: '0.8125rem',
                                                        fontWeight: isSelected ? 600 : 400,
                                                        color: isSelected ? '#12B4A1' : '#94A3B8',
                                                        fontFamily: "'DM Sans', sans-serif",
                                                        ml: 0.5,
                                                    }}>
                                                        {range.label}
                                                    </Typography>
                                                </Box>
                                            )
                                        })}
                                    </Collapse>
                                </Box>
                            ))}
                            {availableRanges.length === 0 && (
                                <Typography sx={{ px: 2.5, py: 2, color: '#64748B', fontSize: '0.875rem', fontFamily: "'DM Sans', sans-serif" }}>
                                    No date ranges available for this store.
                                </Typography>
                            )}
                        </Box>

                        {/* Right Panel - Selection Summary */}
                        <Box sx={{ width: '45%', p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: '#1E293B' }}>
                            <CalendarTodayIcon sx={{ fontSize: 36, color: pendingRanges.length > 0 ? '#12B4A1' : '#334155', mb: 1.5 }} />
                            {pendingRanges.length > 0 ? (
                                <>
                                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#F1F5F9', fontFamily: "'DM Sans', sans-serif", textAlign: 'center', mb: 1 }}>
                                        {pendingRanges.length} range{pendingRanges.length !== 1 ? 's' : ''} selected
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 220, overflowY: 'auto', width: '100%' }}>
                                        {pendingRanges.map((r) => (
                                            <Chip
                                                key={r.filename}
                                                label={r.label}
                                                size="small"
                                                onDelete={() => handleRangeToggle(r)}
                                                sx={{
                                                    fontFamily: "'DM Sans', sans-serif",
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600,
                                                    backgroundColor: 'rgba(18, 180, 161, 0.15)',
                                                    color: '#12B4A1',
                                                    '& .MuiChip-deleteIcon': {
                                                        color: '#12B4A1',
                                                        fontSize: 16,
                                                        '&:hover': { color: '#0596B3' },
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </>
                            ) : (
                                <Typography sx={{ fontSize: '0.875rem', color: '#64748B', fontFamily: "'DM Sans', sans-serif", textAlign: 'center' }}>
                                    Select up to 5 date ranges
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.06)' }} />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', px: 2, py: 1.5, gap: 1, backgroundColor: '#1E293B' }}>
                        <Button
                            onClick={handleDatePopoverClose}
                            sx={{
                                color: '#94A3B8',
                                fontFamily: "'DM Sans', sans-serif",
                                fontWeight: 600,
                                fontSize: '0.8125rem',
                                textTransform: 'uppercase',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleApply}
                            disabled={pendingRanges.length === 0}
                            sx={{
                                backgroundColor: '#12B4A1',
                                color: '#fff',
                                fontFamily: "'DM Sans', sans-serif",
                                fontWeight: 600,
                                fontSize: '0.8125rem',
                                textTransform: 'uppercase',
                                borderRadius: '8px',
                                px: 3,
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: '#139979',
                                    boxShadow: '0 2px 8px rgba(18, 180, 161, 0.3)',
                                },
                            }}
                        >
                            Apply{pendingRanges.length > 0 ? ` (${pendingRanges.length})` : ''}
                        </Button>
                    </Box>
                </Popover>
            </Box>

            {(loading || (isUsersTab && userLoading)) ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
                    <CircularProgress sx={{ color: '#12B4A1' }} />
                </Box>
            ) : isMultiSelect && comparisonData ? (
                <>
                    {/* Selected date ranges chips */}
                    {selectedRanges.length > 1 && (
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#94A3B8', fontFamily: "'DM Sans', sans-serif", mr: 0.5 }}>
                                Showing results for:
                            </Typography>
                            {selectedRanges.map((r) => (
                                <Chip
                                    key={r.filename}
                                    label={r.label}
                                    size="small"
                                    sx={{
                                        fontFamily: "'DM Sans', sans-serif",
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        backgroundColor: 'rgba(18, 180, 161, 0.15)',
                                        color: '#12B4A1',
                                        height: 22,
                                        '& .MuiChip-label': { px: 1 },
                                    }}
                                />
                            ))}
                        </Box>
                    )}

                    {/* Multi-Select Summary Cards + Streams Trend */}
                    <Grid container spacing={3} sx={{ mb: 3, alignItems: 'stretch' }}>
                        {multiSummaryCards.map((card, i) => (
                            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }} key={i}>
                                <MetricCard gradient={CARD_GRADIENTS[i]} sx={{ width: '100%' }}>
                                    <MetricLabel>{card.label}</MetricLabel>
                                    <MetricValue sx={card.small ? {
                                        fontSize: '1.5rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    } : undefined}>{card.value}</MetricValue>
                                </MetricCard>
                            </Grid>
                        ))}
                        {comparisonData?.overallData && comparisonData.overallData.length > 0 && (() => {
                            const od = comparisonData.overallData
                            const peak = od.reduce((max, d) => d.streams > max.streams ? d : max, od[0])
                            const low = od.reduce((min, d) => d.streams < min.streams ? d : min, od[0])
                            const first = od[0].streams
                            const last = od[od.length - 1].streams
                            const pct = first > 0 ? Math.round(((last - first) / first) * 100) : 0
                            const isUp = pct >= 0
                            return (
                                <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }} key="trend">
                                    <MetricCard gradient={CARD_GRADIENTS[3]} sx={{ width: '100%', justifyContent: 'flex-start', pb: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                                            <Box>
                                                <MetricLabel>Streams Trend</MetricLabel>
                                                <MetricValue sx={{ fontSize: '1.75rem', lineHeight: 1.2 }}>
                                                    {avgStreamsPerPeriod.toLocaleString()}
                                                </MetricValue>
                                                <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.7)', fontFamily: "'DM Sans', sans-serif", mt: 0.25 }}>
                                                    avg / period
                                                </Typography>
                                            </Box>
                                            {od.length >= 2 && (
                                                <Box sx={{
                                                    display: 'flex', alignItems: 'center', gap: 0.5,
                                                    px: 1, py: 0.25, borderRadius: '12px',
                                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                                }}>
                                                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#fff', fontFamily: "'DM Sans', sans-serif" }}>
                                                        {isUp ? '\u25B2' : '\u25BC'} {isUp ? '+' : ''}{pct}%
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                        <Box sx={{ width: '100%', flexGrow: 1, minHeight: 50, mt: 0.5 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <AreaChart data={od} margin={{ top: 5, right: 0, bottom: 0, left: 0 }}>
                                                    <defs>
                                                        <linearGradient id="streamsTrendGradient" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="rgba(255,255,255,0.4)" stopOpacity={1} />
                                                            <stop offset="100%" stopColor="rgba(255,255,255,0)" stopOpacity={1} />
                                                        </linearGradient>
                                                    </defs>
                                                    <Tooltip
                                                        contentStyle={{ backgroundColor: 'rgba(0,0,0,0.8)', border: 'none', borderRadius: '6px', padding: '4px 8px', fontSize: '0.7rem', fontFamily: "'DM Sans', sans-serif", color: '#fff' }}
                                                        formatter={(value) => [value.toLocaleString(), 'Streams']}
                                                        labelStyle={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.7)', marginBottom: 2 }}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="streams"
                                                        stroke="rgba(255,255,255,0.8)"
                                                        strokeWidth={2}
                                                        fill="url(#streamsTrendGradient)"
                                                        dot={false}
                                                        activeDot={{ r: 3, fill: '#fff', strokeWidth: 0 }}
                                                    />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 0.5, gap: 0.5 }}>
                                            <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.7)', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.2 }}>
                                                Peak: {peak.streams.toLocaleString()}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.7)', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.2 }}>
                                                Low: {low.streams.toLocaleString()}
                                            </Typography>
                                        </Box>
                                    </MetricCard>
                                </Grid>
                            )
                        })()}
                    </Grid>

                    {/* Per-Song Streams Trend */}
                    {comparisonData.topSongs.length > 0 && (
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={12}>
                                <ChartCard>
                                    <ChartTitle>Per-Song Streams Trend (Top {comparisonData.topSongs.length})</ChartTitle>
                                    <ResponsiveContainer width="100%" height={280}>
                                        <LineChart data={comparisonData.perSongData} margin={{ top: 5, right: 30, bottom: 40, left: 10 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.06)" />
                                            <XAxis dataKey="period" tick={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", fill: '#94A3B8' }} angle={-25} textAnchor="end" height={80} />
                                            <YAxis tick={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", fill: '#94A3B8' }} />
                                            <Tooltip contentStyle={tooltipStyle} itemStyle={{ padding: '2px 0', fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: '#F1F5F9', fontWeight: 700 }} labelStyle={{ fontWeight: 700, fontSize: '0.8rem', color: '#F1F5F9', marginBottom: 6 }} wrapperStyle={{ zIndex: 100 }} />
                                            <Legend wrapperStyle={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: '#94A3B8' }} />
                                            {comparisonData.topSongs.map((song, i) => (
                                                <Line key={song} type="monotone" dataKey={song} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 4 }} />
                                            ))}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </ChartCard>
                            </Grid>
                        </Grid>
                    )}

                    {/* Period Comparison Table */}
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 2, color: '#F1F5F9', fontFamily: "'DM Sans', sans-serif" }}>
                        Period Comparison
                    </Typography>
                    <StyledTableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Period</TableCell>
                                    <TableCell>Total Tracks</TableCell>
                                    <TableCell>Total Streams</TableCell>
                                    <TableCell>Top Track</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedRanges
                                    .map((r) => ({ ...r, parsedDate: parseDDMMYYYY(r.startDate) }))
                                    .sort((a, b) => a.parsedDate - b.parsedDate)
                                    .map((range) => {
                                        const fd = activeMultiFileData[range.filename]
                                        const data = fd?.data || []
                                        const totalStr = data.reduce((sum, t) => sum + t.streams, 0)
                                        const sorted = [...data].sort((a, b) => b.streams - a.streams)
                                        const topTrack = sorted.length > 0 ? sorted[0].title : '-'
                                        return (
                                            <TableRow key={range.filename}>
                                                <TableCell sx={{ fontWeight: 600 }}>{fd?.label || range.label}</TableCell>
                                                <TableCell>{data.length}</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>{totalStr.toLocaleString()}</TableCell>
                                                <TableCell sx={{ color: '#94A3B8' }}>{topTrack}</TableCell>
                                            </TableRow>
                                        )
                                    })
                                }
                            </TableBody>
                        </Table>
                    </StyledTableContainer>
                </>
            ) : (
                <>
                    {/* Filter Tabs */}
                    <Box sx={{ mb: 3, borderRadius: '12px', overflow: 'hidden', display: 'inline-block', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                        <StyledTabs value={activeTab} onChange={handleTabChange}>
                            <StyledTab label="Filter by Songs" />
                            <StyledTab label="Filter by Labels" />
                            {labelUsers.length > 0 && <StyledTab label="Filter by Sub Label Acc." />}
                            <StyledTab label="Store Summary" />
                        </StyledTabs>
                    </Box>

                    {/* User Dropdown — visible only on "Filter by Sub Label Acc." tab */}
                    {isUsersTab && (
                        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#94A3B8', fontFamily: "'DM Sans', sans-serif" }}>
                                Select Sub Label
                            </Typography>
                            <Autocomplete
                                value={labelUsers.find(u => u.user_id === filterUserId) || null}
                                onChange={(event, newValue) => setFilterUserId(newValue ? newValue.user_id : null)}
                                options={labelUsers}
                                getOptionLabel={(option) => option.name || ''}
                                isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Search sub labels..."
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <PersonIcon sx={{ fontSize: 18, color: filterUserId ? '#F1F5F9' : '#94A3B8', ml: 0.5 }} />
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                sx={{
                                    minWidth: 400,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        backgroundColor: 'rgba(30, 41, 59, 0.5)',
                                        backdropFilter: 'blur(20px)',
                                        fontFamily: "'DM Sans', sans-serif",
                                        fontWeight: 600,
                                        color: '#F1F5F9',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                                        '&:hover fieldset': { borderColor: '#12B4A1' },
                                        '&.Mui-focused fieldset': { borderColor: '#12B4A1' },
                                    },
                                    '& .MuiAutocomplete-input': {
                                        fontFamily: "'DM Sans', sans-serif",
                                        fontWeight: 600,
                                        color: '#F1F5F9',
                                        '&::placeholder': { color: '#94A3B8', opacity: 1 },
                                    },
                                    '& .MuiAutocomplete-clearIndicator': { color: '#94A3B8' },
                                    '& .MuiAutocomplete-popupIndicator': { color: '#94A3B8' },
                                }}
                                slotProps={{
                                    paper: {
                                        sx: {
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                                            mt: 1,
                                            minWidth: 300,
                                            backgroundColor: '#1E293B',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                        },
                                    },
                                    listbox: {
                                        sx: {
                                            '& .MuiAutocomplete-option': {
                                                fontFamily: "'DM Sans', sans-serif",
                                                fontWeight: 600,
                                                color: '#F1F5F9',
                                                '&[aria-selected="true"]': { backgroundColor: 'rgba(18, 180, 161, 0.15)' },
                                                '&.Mui-focused': { backgroundColor: 'rgba(255,255,255,0.05)' },
                                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                                            },
                                        },
                                    },
                                    noOptions: {
                                        sx: {
                                            fontFamily: "'DM Sans', sans-serif",
                                            color: '#64748B',
                                            fontSize: '0.875rem',
                                        },
                                    },
                                }}
                                noOptionsText="No sub labels found"
                            />
                        </Box>
                    )}

                    {/* Prompt to select a user when on Users tab with nothing selected */}
                    {isUsersTab && !filterUserId ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '30vh', flexDirection: 'column', gap: 2 }}>
                            <PersonIcon sx={{ fontSize: 64, color: '#334155' }} />
                            <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: '#94A3B8', fontFamily: "'DM Sans', sans-serif" }}>
                                Select a sub label to view their analytics
                            </Typography>
                            <Typography sx={{ fontSize: '0.8rem', color: '#64748B', fontFamily: "'DM Sans', sans-serif" }}>
                                Use the dropdown above to filter data by a specific sub label.
                            </Typography>
                        </Box>
                    ) : isStoresTab ? (
                        <Box>
                            {storeLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '30vh' }}>
                                    <CircularProgress sx={{ color: '#12B4A1' }} />
                                </Box>
                            ) : storeSummary.length === 0 ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '30vh', flexDirection: 'column', gap: 2 }}>
                                    <StorefrontIcon sx={{ fontSize: 64, color: '#334155' }} />
                                    <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: '#94A3B8', fontFamily: "'DM Sans', sans-serif" }}>
                                        No store data available
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.8rem', color: '#64748B', fontFamily: "'DM Sans', sans-serif" }}>
                                        No streaming data found for the selected date range across any store.
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    {/* Summary cards */}
                                    <Grid container spacing={3} sx={{ mb: 3, alignItems: 'stretch' }}>
                                        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                                            <MetricCard gradient={CARD_GRADIENTS[0]} sx={{ width: '100%' }}>
                                                <MetricLabel>Total Stores</MetricLabel>
                                                <MetricValue>{storeSummary.length}</MetricValue>
                                            </MetricCard>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                                            <MetricCard gradient={CARD_GRADIENTS[1]} sx={{ width: '100%' }}>
                                                <MetricLabel>Combined Streams</MetricLabel>
                                                <MetricValue>{storeSummary.reduce((sum, s) => sum + s.totalStreams, 0).toLocaleString()}</MetricValue>
                                            </MetricCard>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                                            <MetricCard gradient={CARD_GRADIENTS[2]} sx={{ width: '100%' }}>
                                                <MetricLabel>Top Store</MetricLabel>
                                                <MetricValue sx={{ fontSize: '1.5rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{storeSummary[0]?.label || '-'}</MetricValue>
                                            </MetricCard>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                                            <MetricCard gradient={CARD_GRADIENTS[3]} sx={{ width: '100%' }}>
                                                <MetricLabel>Total Tracks</MetricLabel>
                                                <MetricValue>{storeSummary.reduce((sum, s) => sum + s.trackCount, 0).toLocaleString()}</MetricValue>
                                            </MetricCard>
                                        </Grid>
                                    </Grid>

                                    {/* Bar chart + Pie chart */}
                                    <Grid container spacing={3} sx={{ mb: 3 }}>
                                        <Grid item xs={12} md={7}>
                                            <ChartCard>
                                                <ChartTitle>Streams by Store</ChartTitle>
                                                <ResponsiveContainer width="100%" height={350}>
                                                    <BarChart data={storeSummary.map(s => ({ name: s.label, streams: s.totalStreams }))} margin={{ top: 5, right: 20, bottom: 60, left: 10 }}>
                                                        <defs>
                                                            <linearGradient id="storeBarGradient" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="0%" stopColor="#12B4A1" stopOpacity={1} />
                                                                <stop offset="100%" stopColor="#0596B3" stopOpacity={0.8} />
                                                            </linearGradient>
                                                        </defs>
                                                        <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", fill: '#94A3B8' }} angle={-45} textAnchor="end" interval={0} height={80} />
                                                        <YAxis tick={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", fill: '#94A3B8' }} />
                                                        <Tooltip contentStyle={tooltipStyle} cursor={false} />
                                                        <Bar dataKey="streams" fill="url(#storeBarGradient)" radius={[4, 4, 0, 0]} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </ChartCard>
                                        </Grid>
                                        <Grid item xs={12} md={5}>
                                            <ChartCard>
                                                <ChartTitle>Store Stream Share</ChartTitle>
                                                <ResponsiveContainer width="100%" height={350}>
                                                    <PieChart>
                                                        <Pie
                                                            data={storeSummary.map(s => ({ name: s.label, value: s.totalStreams }))}
                                                            cx="50%" cy="45%" outerRadius={95} innerRadius={55}
                                                            dataKey="value" paddingAngle={4}
                                                            stroke="rgba(30, 41, 59, 0.8)" strokeWidth={1.5} cornerRadius={8}
                                                        >
                                                            {storeSummary.map((_, index) => (
                                                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: '#F1F5F9', fontSize: '0.85rem', fontWeight: 600 }} />
                                                        <Legend wrapperStyle={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: '#94A3B8' }} />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </ChartCard>
                                        </Grid>
                                    </Grid>

                                    {/* Store details table */}
                                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 2, color: '#F1F5F9', fontFamily: "'DM Sans', sans-serif" }}>
                                        Store Details
                                    </Typography>
                                    <StyledTableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>No.</TableCell>
                                                    <TableCell>Store</TableCell>
                                                    <TableCell>Total Tracks</TableCell>
                                                    <TableCell>Total Streams</TableCell>
                                                    <TableCell>Top Track</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {storeSummary.map((s, i) => (
                                                    <TableRow key={s.store}>
                                                        <TableCell>{i + 1}</TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                {s.logo && <img src={s.logo} alt={s.label} style={{ width: 24, height: 24, objectFit: 'contain' }} />}
                                                                <span style={{ fontWeight: 600 }}>{s.label}</span>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>{s.trackCount}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>{s.totalStreams.toLocaleString()}</TableCell>
                                                        <TableCell sx={{ color: '#94A3B8' }}>{s.topTrack}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </StyledTableContainer>
                                </>
                            )}
                        </Box>
                    ) : (
                    <>
                    {/* Summary Cards */}
                    <Grid container spacing={3} sx={{ mb: 3, alignItems: 'stretch' }}>
                        {summaryCards.map((card, i) => (
                            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }} key={i}>
                                <MetricCard gradient={CARD_GRADIENTS[i]} sx={{ width: '100%' }}>
                                    <MetricLabel>{card.label}</MetricLabel>
                                    <MetricValue sx={card.small ? {
                                        fontSize: '1.5rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    } : undefined}>{card.value}</MetricValue>
                                </MetricCard>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Charts */}
                    {barData.length > 0 && (
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={7}>
                                <ChartCard>
                                    <ChartTitle>{barTitle}</ChartTitle>
                                    <ResponsiveContainer width="100%" height={350}>
                                        <BarChart data={barData} margin={{ top: 5, right: 20, bottom: 60, left: 10 }}>
                                            <defs>
                                                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#12B4A1" stopOpacity={1} />
                                                    <stop offset="100%" stopColor="#0596B3" stopOpacity={0.8} />
                                                </linearGradient>
                                            </defs>
                                            <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", fill: '#94A3B8' }} angle={-45} textAnchor="end" interval={0} height={80} />
                                            <YAxis tick={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", fill: '#94A3B8' }} />
                                            <Tooltip contentStyle={tooltipStyle} cursor={false} />
                                            <Bar dataKey="streams" fill="url(#barGradient)" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </ChartCard>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <ChartCard>
                                    <ChartTitle>{pieTitle}</ChartTitle>
                                    <ResponsiveContainer width="100%" height={350}>
                                        <PieChart>
                                            <Pie
                                                data={pieData}
                                                cx="50%"
                                                cy="45%"
                                                outerRadius={95}
                                                innerRadius={55}
                                                dataKey="value"
                                                paddingAngle={4}
                                                stroke="rgba(30, 41, 59, 0.8)"
                                                strokeWidth={1.5}
                                                cornerRadius={8}
                                                strokeLinejoin="round"
                                            >
                                                {pieData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: '#F1F5F9', fontSize: '0.85rem', fontWeight: 600 }} />
                                            <Legend wrapperStyle={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: '#94A3B8' }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </ChartCard>
                            </Grid>
                        </Grid>
                    )}

                    {/* Top Performing Artists — only on Songs tab */}
                    {showArtistChart && artistData.length > 0 && (
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={12}>
                                <ChartCard>
                                    <ChartTitle>Top 10 Performing Artists</ChartTitle>
                                    <ResponsiveContainer width="100%" height={Math.max(350, artistData.length * 45)}>
                                        <BarChart data={artistData} layout="vertical" margin={{ top: 5, right: 30, bottom: 5, left: 20 }}>
                                            <defs>
                                                <linearGradient id="artistBarGradient" x1="0" y1="0" x2="1" y2="0">
                                                    <stop offset="0%" stopColor="#139979" stopOpacity={1} />
                                                    <stop offset="100%" stopColor="#12B4A1" stopOpacity={0.8} />
                                                </linearGradient>
                                            </defs>
                                            <YAxis dataKey="name" type="category" width={220} tick={{ fontSize: 12, fontFamily: "'DM Sans', sans-serif", fill: '#94A3B8' }} interval={0} />
                                            <XAxis type="number" tick={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", fill: '#94A3B8' }} />
                                            <Tooltip
                                                contentStyle={tooltipStyle}
                                                cursor={false}
                                                content={({ active, payload }) => {
                                                    if (active && payload && payload.length) {
                                                        const data = payload[0].payload
                                                        return (
                                                            <div style={{ ...tooltipStyle, padding: '10px 14px', maxWidth: 300, wordWrap: 'break-word' }}>
                                                                <p style={{ margin: 0, fontWeight: 600, marginBottom: 4 }}>{data.fullName}</p>
                                                                <p style={{ margin: 0, color: '#94A3B8' }}>Streams: {data.streams.toLocaleString()}</p>
                                                            </div>
                                                        )
                                                    }
                                                    return null
                                                }}
                                            />
                                            <Bar dataKey="streams" fill="url(#artistBarGradient)" radius={[0, 4, 4, 0]} barSize={28} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </ChartCard>
                            </Grid>
                        </Grid>
                    )}

                    {/* Data Table */}
                    {tableColumns.length > 0 && (() => {
                        const rowsPerPage = 200
                        const totalPages = Math.ceil(tableRows.length / rowsPerPage)
                        const paginatedRows = tableRows.slice((tablePage - 1) * rowsPerPage, tablePage * rowsPerPage)
                        return (
                            <>
                                <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 2, color: '#F1F5F9', fontFamily: "'DM Sans', sans-serif" }}>
                                    {tableTitle}
                                </Typography>
                                <StyledTableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                {tableColumns.map((col) => (
                                                    <TableCell key={col}>{col}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedRows.map((row) => (
                                                <TableRow key={row.index}>
                                                    <TableCell sx={{ fontWeight: 600 }}>{row.index}</TableCell>
                                                    {row.cells.map((cell, ci) => (
                                                        <TableCell
                                                            key={ci}
                                                            sx={
                                                                ci === 0
                                                                    ? { fontWeight: 600, fontSize: '0.9375rem', color: '#F1F5F9' }
                                                                    : ci === row.cells.length - 1
                                                                        ? { fontWeight: 600 }
                                                                        : { color: '#94A3B8' }
                                                            }
                                                        >
                                                            {cell}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </StyledTableContainer>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '16px', gap: 2 }}>
                                    <Typography sx={{ fontSize: '0.875rem', color: '#94A3B8', fontFamily: "'DM Sans', sans-serif" }}>
                                        Showing {((tablePage - 1) * rowsPerPage) + 1}–{Math.min(tablePage * rowsPerPage, tableRows.length)} of {tableRows.length}
                                    </Typography>
                                    {totalPages > 1 && (
                                        <Pagination
                                            count={totalPages}
                                            page={tablePage}
                                            onChange={(e, page) => setTablePage(page)}
                                            showFirstButton
                                            showLastButton
                                            sx={{
                                                '& .MuiPaginationItem-root': {
                                                    fontFamily: "'DM Sans', sans-serif",
                                                    fontWeight: 600,
                                                    color: '#12B4A1',
                                                },
                                                '& .Mui-selected': {
                                                    background: 'linear-gradient(135deg, #139979 0%, #12B4A1 50%, #0596B3 100%)',
                                                    color: 'white',
                                                    '&:hover': {
                                                        background: 'linear-gradient(135deg, #0f7d62 0%, #0fa08e 100%)',
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                </Box>
                            </>
                        )
                    })()}
                    </>
                    )}
                </>
            )}
        </PageContainer>
    )
}
